import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Col, Container, FormGroup, Label, Row } from "reactstrap";
import AppHeader from "../../../Layout/AppHeader";
import AppSidebar from "../../../Layout/AppSidebar";
import LaddersDataService from "../../../services/LaddersService";
import MsgDataService from "../../../services/MsgService";
import "./ParentMessage.scss";

// Import Advert component for Google ads
import Advert from "../../../components/Advert/Advert";

import { useSelector } from "react-redux";
import { getCommonMessage } from "../../../constants/CommonMessages";
import useParentChildNotificationCount from "../../../hooks/useParentChildNotificationCount";
import { getChildMsgCount } from "../../../utils/parentNotificationCount";
const IMG_URL = process.env.REACT_APP_IMAGE_ASSET_URL;
const SELF_OPTION = "self";

const ParentMessage = () => {
  const lang = useSelector((state) => state.LanguageReducer.current) || 'en';
  const isImpersonating = useSelector((state) => state.ImpersonationReducer?.isImpersonating);
  const parentUser = useSelector((state) => state.UserReducer.userObj);
  const [listLoading, setListLoading] = useState(true);
  const [chatLoading, setChatLoading] = useState(false);
  const [studentList, setStudentList] = useState([]);
  const [studentId, setStudentId] = useState(SELF_OPTION);
  const [list, setList] = useState();
  const [filterList, setFilterList] = useState([]);
  const [searchMsg, setSearchMsg] = useState("");
  const [editChat, setEditChat] = useState(false);
  const [editUserInfo, setEditUserInfo] = useState({});
  const [userChat, setUserChat] = useState([]);
  const [allCount, setAllCount] = useState({});
  const [selfCount, setSelfCount] = useState(0);
  const chatRequestIdRef = useRef(0);
  const editUserInfoRef = useRef({});
  const scrollContainerRef = useRef(null);
  const chatLoadingRef = useRef(false);
  const editChatRef = useRef(false);
  const childDropdownRef = useRef(null);
  const [openChildDropdown, setOpenChildDropdown] = useState(false);
  const [studentListReady, setStudentListReady] = useState(false);
  const fetchedStudentRef = useRef("");
  const { refresh: refreshParentNotificationCount } = useParentChildNotificationCount({
    autoRefresh: false
  });

  const labels = {
    en: {
      messages: 'Messages',
      selectYourChild: 'Select Your Child',
      self: 'Self',
      searchPlaceholder: 'Search contact and messages',
      youHave: 'You have',
      no: 'no',
      newMessageSingular: 'new message',
      newMessagePlural: 'new messages',
      emptyHeading: "Let's begin a conversation",
      emptyText: 'Pick a person from the menu on the left and start your conversation',
      download: 'Download Attachment',
    },
    es: {
      messages: 'Mensajes',
      selectYourChild: 'Selecciona a tu hijo',
      self: 'Yo',
      searchPlaceholder: 'Buscar contactos y mensajes',
      youHave: 'Tienes',
      no: 'No tienes',
      newMessageSingular: 'mensaje nuevo',
      newMessagePlural: 'mensajes nuevos',
      emptyHeading: 'Comencemos una conversación',
      emptyText: 'Elige a una persona del menú de la izquierda y comienza tu conversación',
      download: 'Descargar archivo',
    },
  };
  const t = (key) => (labels[lang] || labels.en)[key];
  const isSelfSelected = studentId === SELF_OPTION;
  const selectedStudent = studentList.find(
    (item) => String(item.id) === String(studentId)
  );
  const selectedLabel = isSelfSelected
    ? t("self")
    : selectedStudent?.name || t("self");
  const selectedCount = !studentListReady
    ? 0
    : isSelfSelected
      ? selfCount
      : getChildMsgCount(selectedStudent);
  const isIncomingMessage = (item) =>
    isSelfSelected
      ? String(item?.src_id) === String(editUserInfo?.src_id)
      : String(item?.src_id) === String(studentId);

  useEffect(() => {
    editUserInfoRef.current = editUserInfo;
  }, [editUserInfo]);

  useEffect(() => {
    chatLoadingRef.current = chatLoading;
  }, [chatLoading]);

  useEffect(() => {
    editChatRef.current = editChat;
  }, [editChat]);

  useEffect(() => {
    const closeDropdown = (event) => {
      if (
        childDropdownRef.current &&
        !childDropdownRef.current.contains(event.target)
      ) {
        setOpenChildDropdown(false);
      }
    };
    document.addEventListener("mousedown", closeDropdown);
    return () => document.removeEventListener("mousedown", closeDropdown);
  }, []);

  const getStudentWiseunreadCount = useCallback((id) => {
    const targetId = id || studentId;
    if (!targetId) return;
    MsgDataService.getUnreadCountForParent(targetId)
      .then((res) => {
        setAllCount(res?.data);
      })
      .catch(() => {});
  }, [studentId]);

  const getStudentList = useCallback(() => {
    LaddersDataService.getParentRoleStudentList()
      .then((res) => {
        if (res?.status === 200) {
          setStudentList(res?.data?.list || []);
          setSelfCount(Number(res?.data?.self) || 0);
        } else {
          setStudentList([]);
          setSelfCount(0);
        }
      })
      .catch(() => {
        setStudentList([]);
        setSelfCount(0);
      })
      .finally(() => {
        setStudentListReady(true);
      });
  }, []);

  const getReadCountbyDate = useCallback((id, student_id) => {
    const readRequest = student_id === SELF_OPTION
      ? MsgDataService.readParentSelfMessage(parentUser?.id, id)
      : MsgDataService.readCountDate({
          src_id: id,
          child_id: student_id
        });
    readRequest
      .then((res) => {
        if (res?.status === 200) {
          refreshParentNotificationCount();
          getStudentList();
          getStudentWiseunreadCount(student_id);
        }
      })
      .catch(() => {});
  }, [getStudentList, getStudentWiseunreadCount, parentUser?.id, refreshParentNotificationCount]);

  const openClickedContact = useCallback((item, { force = false } = {}) => {
    if (!item?.src_id || !studentId) return;
    if (
      !force &&
      editUserInfoRef.current?.src_id === item.src_id &&
      editChatRef.current &&
      !chatLoadingRef.current
    ) {
      return;
    }

    const requestId = ++chatRequestIdRef.current;
    setEditUserInfo(item);
    setEditChat(true);
    setUserChat([]);
    setChatLoading(true);

    if (!isImpersonating) {
      getReadCountbyDate(item.src_id, studentId);
    }

    MsgDataService.getMessageParentListPerUser(1, item.src_id, studentId)
      .then((data) => {
        if (requestId !== chatRequestIdRef.current) return;
        const msgObjArr = data?.data?.messages || [];
        setUserChat(msgObjArr);
      })
      .catch(() => {
        if (requestId !== chatRequestIdRef.current) return;
        setUserChat([]);
      })
      .finally(() => {
        if (requestId === chatRequestIdRef.current) {
          setChatLoading(false);
        }
      });
  }, [getReadCountbyDate, isImpersonating, studentId]);

  const getUserList = useCallback(() => {
    if (!studentId) return;
    setListLoading(true);
    MsgDataService.getParentMessageList(studentId)
      .then((res) => {
        if (res?.status === 200) {
          const messageList = res?.data?.data || [];
          setList(messageList);
          setFilterList(messageList);
        } else {
          setList([]);
          setFilterList([]);
        }
      })
      .catch(() => {
        setList([]);
        setFilterList([]);
      })
      .finally(() => {
        setListLoading(false);
      });
  }, [studentId]);

  useEffect(() => {
    getStudentList();
  }, [getStudentList]);

  const handleChangeStudent = useCallback((value) => {
    setOpenChildDropdown(false);
    if (String(value) === String(studentId)) return;
    setSearchMsg("");
    setEditChat(false);
    setChatLoading(false);
    setListLoading(true);
    setUserChat([]);
    setEditUserInfo({});
    setList([]);
    setFilterList([]);
    setStudentId(value);
  }, [studentId]);

  useEffect(() => {
    if (!studentId) return;
    if (fetchedStudentRef.current === String(studentId)) return;
    fetchedStudentRef.current = String(studentId);
    getUserList();
    getStudentWiseunreadCount(studentId);
  }, [studentId, getUserList, getStudentWiseunreadCount]);

  // Re-fetch open conversation when language changes
  useEffect(() => {
    if (editChat && editUserInfoRef.current?.src_id && studentId) {
      openClickedContact(editUserInfoRef.current, { force: true });
    }
    // Only re-run on language change to avoid duplicate fetches
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const handleChangeSearchMsg = useCallback((e) => {
    const value = e.target.value;
    setSearchMsg(value);
    const filterArr = (list || []).filter(
      (item) =>
        item.name
          .toLowerCase()
          .replace(/ +/g, "")
          .match(value.toLowerCase().replace(/ +/g, "")) ||
        item.src_role
          .toLowerCase()
          .replace(/ +/g, "")
          .match(value.toLowerCase().replace(/ +/g, ""))
    );
    setFilterList(filterArr);
  }, [list]);

  const getCountData = useCallback((item) => {
    if (!allCount) return "";
    const message = Object.keys(allCount).map((key) => {
      return allCount[key][item.src_id];
    });
    const data = message.filter((x) => x !== undefined);

    if (data && data[0] && data[0] !== 0) {
      return <div className="message-notification">{`${data[0]}`}</div>;
    }
    return "";
  }, [allCount]);

  useEffect(() => {
    if (!chatLoading && userChat.length > 0) {
      scrollContainerRef?.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [chatLoading, userChat.length]);

  return (
    <>
      <AppHeader />
      <div className="app-main">
        <AppSidebar />
        <div className="app-main__outer">
          <div className="app-main__inner parentMessage">
            {/* <div className="message-ad-header">
              <div className="header-content">
                <div className="ad-section-centered">
                  <Advert />
                </div>
              </div>
            </div> */}

            <div className="msg-main">
              <Container fluid>
                <Row className="studentWrap">
                    <Col lg={4} md={6}>
                      <FormGroup className="text-left studentSelect">
                        <Label className="child-select-label">{t('selectYourChild')}</Label>
                        <div className="child-select-dropdown" ref={childDropdownRef}>
                          <button
                            type="button"
                            className="child-select-trigger"
                            onClick={() => setOpenChildDropdown((open) => !open)}
                          >
                            <span className="child-select-name">{selectedLabel}</span>
                            {studentListReady && selectedCount > 0 && (
                              <span className="child-select-count">{selectedCount}</span>
                            )}
                          </button>
                          {openChildDropdown && (
                            <ul className="child-select-menu">
                              <li
                                className={isSelfSelected ? "active" : ""}
                                onClick={() => handleChangeStudent(SELF_OPTION)}
                              >
                                <span className="child-select-name">{t("self")}</span>
                                {studentListReady && selfCount > 0 && (
                                  <span className="child-select-count">{selfCount}</span>
                                )}
                              </li>
                              {studentList.map((item) => {
                                const childCount = getChildMsgCount(item);
                                return (
                                  <li
                                    key={item.id}
                                    className={
                                      String(item.id) === String(studentId)
                                        ? "active"
                                        : ""
                                    }
                                    onClick={() => handleChangeStudent(String(item.id))}
                                  >
                                    <span className="child-select-name">{item.name}</span>
                                    {studentListReady && childCount > 0 && (
                                      <span className="child-select-count">
                                        {childCount}
                                      </span>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>
                      </FormGroup>
                    </Col>
                  </Row>
                <div className="msgWrapper">
                  <Row>
                    <Col lg={4} md={6}>
                      <div className="messageSidebar">
                        <div className="sidebar-header">
                          <span className="title">{t('messages')}</span>
                          <div className="contactSearchWrap">
                            <FontAwesomeIcon
                              icon={faSearch}
                              className="searchContactIcon"
                            />
                            <input
                              type="text"
                              className="form-control search-input contactSearch"
                              placeholder={t('searchPlaceholder')}
                              name="searchMsg"
                              value={searchMsg || ""}
                              autoComplete="off"
                              onChange={handleChangeSearchMsg}
                              disabled={listLoading}
                            />
                          </div>
                        </div>
                        <div className="list-wrap">
                          {studentListReady && (
                          <p className="messageCount">
                            {lang === 'es' ? (
                              selectedCount > 0 ? (
                                <>
                                  {t('youHave')} {" "}
                                  <span className={"countText green"}>
                                    {selectedCount} {selectedCount === 1 ? t('newMessageSingular') : t('newMessagePlural')}
                                  </span>
                                </>
                              ) : (
                                <>
                                  {t('no')} {" "}
                                  <span className={'countText'}>
                                    {t('newMessagePlural')}
                                  </span>
                                </>
                              )
                            ) : (
                              <>
                                {t('youHave')} {" "}
                                <span
                                  className={`countText ${
                                    selectedCount > 0 ? "green" : ""
                                  }`}
                                >
                                  {selectedCount > 0
                                    ? selectedCount
                                    : t('no')} {" "}
                                  {selectedCount === 1 ? t('newMessageSingular') : t('newMessagePlural')}
                                </span>
                              </>
                            )}
                          </p>
                          )}
                          {listLoading ? (
                            <div className="list-loading-wrap">
                              <div className="chat-blue-loader" aria-label="Loading messages" />
                            </div>
                          ) : filterList && filterList.length > 0 ? (
                            filterList.map((item, index) => (
                              <div
                                className="list"
                                key={item?.src_id || index}
                                onClick={() => openClickedContact(item)}
                              >
                                <img
                                  className="profile-img"
                                  src={
                                    item?.profile_image ||
                                    IMG_URL + "placeholder.jpg"
                                  }
                                  alt=""
                                />
                                <div className="info">
                                  <span className="user">{item.name}</span>
                                  <span className="text">
                                    {item.src_role}
                                  </span>
                                </div>
                                <div>
                                  <div className="time">
                                    {getCountData(item)}
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="list-empty-wrap">
                              {getCommonMessage('noDataFound', lang)}
                            </div>
                          )}
                        </div>
                      </div>
                    </Col>

                    {/* Edit Screen */}
                    {editChat ? (
                        <Col lg={8} md={6}>
                          <div className="content">
                            <header>
                              <img
                                src={
                                  editUserInfo?.profile_image ||
                                  IMG_URL + "placeholder.jpg"
                                }
                                alt=""
                              />
                              <div className="info d-flex flex-column">
                                <span className="user">
                                  {editUserInfo?.name}
                                </span>
                              </div>
                            </header>
                            <div className="message-wrap">
                              {chatLoading ? (
                                <div className="chat-loading-wrap">
                                  <div className="chat-blue-loader" aria-label="Loading conversation" />
                                </div>
                              ) : (
                                userChat && userChat.length > 0
                                ? userChat.map((item, index) => {
                                    return index === userChat.length - 1 ? (
                                      <div
                                        className="userChatBlock"
                                        ref={scrollContainerRef}
                                        key={index}
                                      >
                                        <div
                                          className={`message-list ${
                                            isIncomingMessage(item)
                                              ? ""
                                              : "me"
                                          }`}
                                        >
                                          <div className="msg">
                                            <p>
                                              <div>
                                                {item.message_body}
                                              </div>
                                              <a
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                  color: "blue",
                                                  textDecorationLine:
                                                    "Underline",
                                                  marginRight: "10px",
                                                }}
                                                href={item?.attachment_url}
                                              >
                                                {item?.attachment_url ? t('download') : " "}
                                              </a>
                                            </p>
                                          </div>
                                          <div className="time">
                                            {moment(item.date_sent).format(
                                              "Do MMM | hh:mm a"
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    ) : (
                                      <div
                                        className="userChatBlock"
                                        key={index}
                                      >
                                        <div
                                          className={`message-list ${
                                            isIncomingMessage(item)
                                              ? ""
                                              : "me"
                                          }`}
                                        >
                                          <div className="msg">
                                            <p>
                                              <div>
                                                {item.message_body}
                                              </div>
                                              <a
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                  color: "blue",
                                                  textDecorationLine:
                                                    "Underline",
                                                  marginRight: "10px",
                                                }}
                                                href={item?.attachment_url}
                                              >
                                                {item?.file_name}
                                              </a>
                                            </p>
                                          </div>
                                          <div className="time">
                                            {moment(item.date_sent).format(
                                              "Do MMM | hh:mm a"
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })
                                : ""
                              )}
                            </div>
                          </div>
                        </Col>
                    ) : (
                      <Col lg={8} md={6}>
                        <div className="noOpenChatScreen">
                          <div className="nochatBlock">
                            <img
                              src={IMG_URL + "initChatImg.png"}
                              alt="initChatImg"
                              className="img-fluid"
                            />
                            <p>{t('emptyHeading')}</p>
                            <div className="chatText">
                              {t('emptyText')}
                            </div>
                          </div>
                        </div>
                      </Col>
                    )}
                  </Row>
                </div>
              </Container>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ParentMessage;